import React from 'react';

import { Resources } from '@ariksa/inventory-core';
import { NativeResources, PackageCategory } from '@ariksa/inventory-core/api';
import { HStack, Box, Stack, Center } from '@chakra-ui/react';
import {
  forEach,
  isEmpty,
  join,
  map,
  slice,
  sum,
  toArray,
  truncate,
  values,
} from 'lodash';
import { customTheme } from 'theme';

import {
  EllipsisText,
  PlusCountTag,
  renderTooltipFieldValue,
  StackedCell,
  Tag,
  WithResourceIcon,
} from 'components/DataDisplay';
import { plusCountTagStyles } from 'components/DataDisplay/Tag/styles';
import { CustomTooltip } from 'components/DataDisplay/Tooltip/CustomTooltip';
import { IconTypes, LinuxIcon } from 'components/Icons';
import { getIcon } from 'components/Icons/Components';
import { ResourceTypeIconTooltip } from 'containers/App/hooks/useResourceType';

const renderCloudEntity = (type, count) => (
  <HStack spacing={1}>
    <Box boxSize={5}>{getIcon(type)}</Box>
    <Box>({count})</Box>
  </HStack>
);

export const renderCloudEntities = ({ value, showTotal = false }) => {
  if (showTotal && value?.total) return <Box>({value.total})</Box>;
  if (!sum(values(value)))
    return <Box color={customTheme.colors.gray['100']}>No Entities</Box>;
  return (
    <HStack>
      {!!value?.aws && renderCloudEntity(IconTypes.Aws, value?.aws)}
      {!!value?.azure && renderCloudEntity(IconTypes.Azure, value?.azure)}
      {!!value?.gcp && renderCloudEntity(IconTypes.Gcp, value?.gcp)}
    </HStack>
  );
};

export const renderPackagesImpacted = ({ value }) => {
  const data = new Set();
  forEach(value, o => data.add(o));
  const renderTag = item => (
    <CustomTooltip label={item}>
      <Box>{truncate(item, { length: 20 })}</Box>
    </CustomTooltip>
  );
  const packages = toArray(data);
  return !isEmpty(data) ? (
    <Stack spacing={1}>
      <HStack spacing={1}>{packages?.[0] && renderTag(packages?.[0])}</HStack>
      <HStack spacing={1}>
        {packages?.[1] && renderTag(packages?.[1])}
        {packages?.length > 2 && (
          <CustomTooltip
            label={
              <Stack>
                {map(slice(packages, 2), o => (
                  <Box>{o as string}</Box>
                ))}
              </Stack>
            }
          >
            <PlusCountTag count={packages?.length - 2} />
          </CustomTooltip>
        )}
      </HStack>
    </Stack>
  ) : (
    '-'
  );
};

export const renderPackageType = value =>
  value === PackageCategory.ApplicationPackage
    ? 'Technology'
    : value === PackageCategory.LibraryPackage
    ? 'Library'
    : 'OS';

export const renderImageName = (row, resourceType) => {
  const name = row?.name ?? join(row?.image_tags, ', ');
  const id =
    resourceType === NativeResources.ElasticContainerRegistry
      ? row?.image_digest
      : row?.resource_id;
  return (
    <WithResourceIcon
      resourceType={
        resourceType === Resources.Image ? IconTypes.MachineImage : resourceType
      }
      iconTooltip={<ResourceTypeIconTooltip resourceType={row?.native_name} />}
      tooltip={
        <Stack>
          {renderTooltipFieldValue('Name', name)}
          {renderTooltipFieldValue(
            resourceType === NativeResources.ElasticContainerRegistry
              ? 'URI'
              : 'ID',
            id,
          )}
        </Stack>
      }
    >
      <StackedCell
        upper={name}
        lower={id}
        showUpperTooltip={false}
        showLowerTooltip={false}
      />
    </WithResourceIcon>
  );
};

export const getScanningStatus = row => {
  switch (row?.status) {
    case 'running':
      return 'Scanning';
    case 'waiting':
      return 'Queued for Scan';
    default:
      return 'test';
  }
};

export const renderOSNameAndIcon = data => {
  return (
    <HStack>
      <Box boxSize={5}>
        <Center>{getIcon(data?.os_name, undefined, <LinuxIcon />)}</Center>
      </Box>
      <EllipsisText
        label={
          (!!data?.os_name ? data?.os_name : 'OS Unknown') +
          ' ' +
          (data?.os_version ?? '')
        }
        showTooltip={false}
      />
    </HStack>
  );
};
